import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ModalComponent } from '../ui/modal/modal.component';
import { ButtonComponent } from '../ui/button/button.component';
import { ResultsDayService } from 'src/app/services/results-day.service';
import { ResultsDayStore } from 'src/app/stores/results-day-store';
import { TlhResultsDay } from 'src/app/types/results-day-types';

/** GCSE numeric grading runs 1 (lowest) to 9 (highest). */
const GRADES = [1, 2, 3, 4, 5, 6, 7, 8, 9];

/** 'flow' is the normal 4-step entry journey; the other two are one-off info screens. */
type ResultsDayViewState = 'flow' | 'not-live' | 'already-entered';

@Component({
  selector: 'app-results-day-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent, ButtonComponent],
  templateUrl: './results-day-modal.component.html',
  styleUrl: './results-day-modal.component.scss',
})
export class ResultsDayModalComponent implements OnInit {
  private resultsDayService = inject(ResultsDayService);
  private resultsDayStore = inject(ResultsDayStore);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  readonly grades = GRADES;

  isOpen = signal(false);
  currentStep = signal(1);
  resultsDay = signal<TlhResultsDay | null>(null);
  isSaving = signal(false);
  viewState = signal<ResultsDayViewState>('flow');

  startingLanguageGrade = signal<number | null>(null);
  startingLiteratureGrade = signal<number | null>(null);
  finalLanguageGrade = signal<number | null>(null);
  finalLiteratureGrade = signal<number | null>(null);

  /**
   * Sourced from the ResultsDayStore, which is populated once per app load
   * from the auth-user bootstrap response — no dedicated "is it active" HTTP
   * call here, so this never adds an extra request per page/login. Also
   * listens for on-demand open requests (e.g. a campaign iframe naming a
   * specific results day id via postMessage) for the lifetime of the modal.
   */
  ngOnInit(): void {
    this.resultsDayService.openRequests
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resultsDayId) => this.openForId(resultsDayId));

    const results_day = this.resultsDayStore.resultsDay();
    const entry = this.resultsDayStore.entry();

    // Nothing currently live, or this user already finished/declined it.
    if (!results_day || entry?.completed_at || entry?.declined_at) {
      return;
    }

    this.resultsDay.set(results_day);

    if (entry) {
      this.currentStep.set(entry.current_step);
      this.startingLanguageGrade.set(entry.starting_language_grade);
      this.startingLiteratureGrade.set(entry.starting_literature_grade);
      this.finalLanguageGrade.set(entry.final_language_grade);
      this.finalLiteratureGrade.set(entry.final_literature_grade);
    }

    this.isOpen.set(true);
  }

  /**
   * Opens the popup for a specific results day id, regardless of what the
   * auto-popup logic above decided — used for explicit "I want to enter"
   * triggers (e.g. a campaign iframe CTA), so a prior skip/decline doesn't
   * block re-entry. Only an already-completed entry short-circuits it, since
   * there's nothing left to do at that point.
   */
  openForId(resultsDayId: number): void {
    this.resultsDayService.getById(resultsDayId).subscribe({
      next: ({ results_day, entry }) => {
        if (!results_day) {
          this.viewState.set('not-live');
          this.isOpen.set(true);
          return;
        }

        if (entry?.completed_at) {
          this.viewState.set('already-entered');
          this.isOpen.set(true);
          return;
        }

        this.viewState.set('flow');
        this.resultsDay.set(results_day);
        this.currentStep.set(entry?.current_step ?? 1);
        this.startingLanguageGrade.set(entry?.starting_language_grade ?? null);
        this.startingLiteratureGrade.set(entry?.starting_literature_grade ?? null);
        this.finalLanguageGrade.set(entry?.final_language_grade ?? null);
        this.finalLiteratureGrade.set(entry?.final_literature_grade ?? null);
        this.isOpen.set(true);
      },
      error: () => {},
    });
  }

  /** Splits the admin-entered title around a "£<amount>" so it can be highlighted, matching the design — falls back to one plain span if there's no match. */
  get titleParts(): { before: string; amount: string | null; after: string } {
    const title = this.resultsDay()?.title ?? '';
    const match = title.match(/£[\d,]+/);

    if (!match || match.index === undefined) {
      return { before: title, amount: null, after: '' };
    }

    return {
      before: title.slice(0, match.index),
      amount: match[0],
      after: title.slice(match.index + match[0].length),
    };
  }

  get canContinueStartingGrades(): boolean {
    return this.startingLanguageGrade() !== null && this.startingLiteratureGrade() !== null;
  }

  get canContinueFinalGrades(): boolean {
    return this.finalLanguageGrade() !== null && this.finalLiteratureGrade() !== null;
  }

  /**
   * The modal's (close) output also fires from the shared ModalComponent's
   * own Escape-key handler regardless of step, not just the step-1 X/skip
   * link — so only treat this as an explicit decline ("not interested") when
   * it happens on step 1. Closing mid-flow (steps 2-4) just hides the popup
   * for this session; progress is kept and it resumes next login. Hides
   * immediately either way; persisting the decline is best-effort and
   * doesn't block the UI.
   */
  close(): void {
    this.isOpen.set(false);

    if (this.viewState() !== 'flow') {
      this.viewState.set('flow');
      return;
    }

    const resultsDay = this.resultsDay();
    if (!resultsDay || this.currentStep() !== 1) return;

    // The store only tracks the one results day the automatic popup cares
    // about — an openForId() call can be for a different one entirely.
    if (this.resultsDayStore.resultsDay()?.id === resultsDay.id) {
      this.resultsDayStore.markDeclined();
    }
    this.resultsDayService.decline(resultsDay.id).subscribe({ error: () => {} });
  }

  enterMyGrades(): void {
    const resultsDay = this.resultsDay();
    if (!resultsDay || this.isSaving()) return;

    this.isSaving.set(true);
    this.resultsDayService.start(resultsDay.id).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.currentStep.set(2);
      },
      error: () => this.isSaving.set(false),
    });
  }

  previous(): void {
    this.currentStep.update((step) => Math.max(1, step - 1));
  }

  continueFromStartingGrades(): void {
    const resultsDay = this.resultsDay();
    if (!resultsDay || !this.canContinueStartingGrades || this.isSaving()) return;

    this.isSaving.set(true);
    this.resultsDayService
      .saveEntry(resultsDay.id, {
        current_step: 3,
        starting_language_grade: this.startingLanguageGrade()!,
        starting_literature_grade: this.startingLiteratureGrade()!,
      })
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.currentStep.set(3);
        },
        error: () => this.isSaving.set(false),
      });
  }

  continueFromFinalGrades(): void {
    const resultsDay = this.resultsDay();
    if (!resultsDay || !this.canContinueFinalGrades || this.isSaving()) return;

    this.isSaving.set(true);
    this.resultsDayService
      .saveEntry(resultsDay.id, {
        current_step: 4,
        final_language_grade: this.finalLanguageGrade()!,
        final_literature_grade: this.finalLiteratureGrade()!,
      })
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.currentStep.set(4);
        },
        error: () => this.isSaving.set(false),
      });
  }

  finishEntry(): void {
    const resultsDay = this.resultsDay();
    if (!resultsDay || this.isSaving()) return;

    this.isSaving.set(true);
    this.resultsDayService.finish(resultsDay.id).subscribe({
      next: ({ tlh_campaign_id }) => {
        this.isSaving.set(false);
        this.isOpen.set(false);
        if (this.resultsDayStore.resultsDay()?.id === resultsDay.id) {
          this.resultsDayStore.markCompleted();
        }
        this.router.navigateByUrl(`/campaigns/${tlh_campaign_id}`);
      },
      error: () => this.isSaving.set(false),
    });
  }
}
